import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ShieldCheck, Search, Trash2, Pencil, X, KeyRound, Lock, Clock3, FolderTree } from 'lucide-react';
import { Link } from 'react-router-dom';

// Managers' "everything" view (Phase 4). The extension shows a manager only their
// own + shared items; here a real manager sees EVERY vault item and can fully
// manage passkeys and passwords. TOTP codes are shown read-only — they are
// created/edited on the main dashboard and deleted directly in Salesforce.
const vault = (path, opts = {}) =>
  fetch(`/api/passkey-vault${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });

const TYPE_BADGE = {
  TOTP: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Passkey: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Password: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminVaultPage = () => {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [editing, setEditing] = useState(null); // the item being edited, or null

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await vault('/admin/items');
      if (r.status === 403) throw new Error('Réservé aux managers.');
      if (!r.ok) throw new Error('Chargement du coffre impossible.');
      setItems(await r.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const r = await vault('/folders');
      if (r.ok) setFolders(await r.json());
    } catch { /* folders optional */ }
  }, []);

  useEffect(() => { loadItems(); loadFolders(); }, [loadItems, loadFolders]);

  const deleteItem = async (it) => {
    if (it.type === 'TOTP') return;
    if (!window.confirm(`Supprimer « ${it.name} » ? Cette action est irréversible.`)) return;
    const r = await vault(`/admin/items/${it.id}`, { method: 'DELETE' });
    if (!r.ok) { setError('Suppression impossible.'); return; }
    await loadItems();
  };

  const filtered = items
    .filter((i) => typeFilter === 'ALL' || i.type === typeFilter)
    .filter((i) => {
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return [i.name, i.username, i.creatorEmail, i.folderName, i.deliveryName, i.account]
        .some((v) => (v || '').toLowerCase().includes(q));
    });

  const counts = items.reduce((acc, i) => { acc[i.type] = (acc[i.type] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 py-4">
            <Link to="/" className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Retour">
              <ArrowLeft className="w-6 h-6 text-gray-500" />
            </Link>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-blue-500" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Coffre — vue complète</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Vue manager : tous les éléments du coffre. Vous pouvez modifier et supprimer les passkeys et les mots de passe.
          Les codes TOTP se gèrent sur le tableau de bord.
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 border border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher (nom, utilisateur, dossier, créateur…)"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
          </div>
          <div className="flex flex-wrap gap-2">
            {['ALL', 'Passkey', 'Password', 'TOTP'].map((t) => (
              <button key={t} onClick={() => setTypeFilter(t)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border ${typeFilter === t
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600'}`}>
                {t === 'ALL' ? `Tous (${items.length})` : `${t} (${counts[t] || 0})`}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  {['Nom', 'Type', 'Utilisateur', 'Dossier', 'Créateur', 'Partagé avec', 'Modifié', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">Chargement…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">Aucun élément</td></tr>
                ) : filtered.map((i) => (
                  <tr key={i.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        {i.type === 'Passkey' ? <KeyRound className="w-4 h-4 text-purple-500" />
                          : i.type === 'Password' ? <Lock className="w-4 h-4 text-emerald-500" />
                          : <Clock3 className="w-4 h-4 text-blue-500" />}
                        <span className="truncate max-w-[220px]">{i.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[i.type] || ''}`}>{i.type}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 truncate max-w-[200px]">{i.username || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {i.folderName ? (
                        <span className="inline-flex items-center gap-1"><FolderTree className="w-3 h-3 text-gray-400" />{i.folderName}</span>
                      ) : (i.deliveryName ? <span className="text-gray-400">Delivery : {i.deliveryName}</span> : '—')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 truncate max-w-[180px]">{i.creatorEmail || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{i.sharedWith?.length ? `${i.sharedWith.length}` : '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDate(i.updatedAt)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {i.type === 'TOTP' ? (
                        <span className="text-xs text-gray-400" title="Les TOTP se gèrent sur le tableau de bord">Dashboard</span>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditing(i)} className="p-1.5 text-gray-400 hover:text-blue-500" title="Modifier">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteItem(i)} className="p-1.5 text-gray-400 hover:text-red-500" title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editing && (
        <EditModal
          item={editing}
          folders={folders}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await loadItems(); }}
          onError={setError}
        />
      )}
    </div>
  );
};

// Edit a passkey or a password (name / username / URL / password reset / folder /
// share list). Only changed fields are sent; a blank password keeps the existing.
const EditModal = ({ item, folders, onClose, onSaved, onError }) => {
  const [name, setName] = useState(item.name || '');
  const [username, setUsername] = useState(item.username || '');
  const [loginUrl, setLoginUrl] = useState(item.loginUrl || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [folderId, setFolderId] = useState(item.folderId || '');
  const [sharedWith, setSharedWith] = useState((item.sharedWith || []).join(', '));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        username,
        loginUrl,
        folderId: folderId || null,
        sharedWith: sharedWith.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (password) body.password = password;
      const r = await vault(`/admin/items/${item.id}`, { method: 'POST', body: JSON.stringify(body) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || 'Enregistrement impossible.'); }
      await onSaved();
    } catch (e) { onError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[item.type] || ''}`}>{item.type}</span>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Modifier</h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Nom">
            <input value={name} onChange={(e) => setName(e.target.value)} className="input" />
          </Field>
          <Field label="Nom d'utilisateur">
            <input value={username} onChange={(e) => setUsername(e.target.value)} className="input" placeholder="—" />
          </Field>
          <Field label="Site de connexion (URL)">
            <input value={loginUrl} onChange={(e) => setLoginUrl(e.target.value)} className="input" placeholder="https://…" />
          </Field>
          <Field label={item.hasPassword ? 'Mot de passe (laisser vide pour conserver)' : 'Mot de passe'}>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                className="input pr-16" placeholder={item.hasPassword ? '•••••••• (inchangé)' : 'Définir un mot de passe'} autoComplete="new-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                {showPw ? 'Masquer' : 'Afficher'}
              </button>
            </div>
          </Field>
          <Field label="Dossier">
            <select value={folderId} onChange={(e) => setFolderId(e.target.value)} className="input">
              <option value="">Aucun dossier</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </Field>
          <Field label="Partagé avec (emails séparés par des virgules)">
            <input value={sharedWith} onChange={(e) => setSharedWith(e.target.value)} className="input" placeholder="a@solganeo.com, b@solganeo.com" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">Annuler</button>
          <button onClick={save} disabled={saving || !name.trim()}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
    {children}
  </div>
);

export default AdminVaultPage;
